import { createSelector, createSlice } from "@reduxjs/toolkit";
import type {
    Account,
    AccountType,
    Bill,
    Debt,
    Expense,
    FinancialCategory,
    Income,
    MinimalAccount,
    Saving,
    SerializableUserSubscription,
    Spender,
    Subscription,
    UserSubscription,
} from "../global/types";
import { Timestamp } from "firebase/firestore";

type UserDataSliceState = {
    subscription: SerializableUserSubscription | null;
    accounts: Account[] | null;
    spenders: Spender[] | null;
    financialCategories: {
        income: Income[] | null;
        expenses: Expense[] | null;
        bills: Bill[] | null;
        savings: Saving[] | null;
        subscriptions: Subscription[] | null;
        debts: Debt[] | null;
    },

}

const initialState: UserDataSliceState = {
    subscription: null,
    accounts: null,
    spenders: null,
    financialCategories: {
        income: null,
        expenses: null,
        bills: null,
        savings: null,
        subscriptions: null,
        debts: null,
    }
}

const userDataSlice = createSlice({
    name: "userData",
    initialState,
    reducers: {
        setSubscription: (state, action) => {
            state.subscription = action.payload;
        },
        setAccounts: (state, action) => {
            state.accounts = action.payload
        },
        setSpenders: (state, action) => { state.spenders = action.payload; },
        setFinancialCategory: (state, action) => {
            const { type, data }: {
                type: FinancialCategory | 'spenders';
                data: any[];
            } = action.payload;
            if (type === 'spenders') {
                state.spenders = data as Spender[];
            } else {
                (state.financialCategories as Record<string, any>)[type as keyof typeof state.financialCategories] = data;
            }
        },
    },
    selectors: {
        _selectSubscription: (state: UserDataSliceState): UserSubscription | null => {
            return state.subscription ? {
                ...state.subscription,
                startDate: Timestamp.fromMillis(state.subscription.startDate),
                endDate: state.subscription.endDate ? Timestamp.fromMillis(state.subscription.endDate) : null,
                trialEndDate: state.subscription.trialEndDate ? Timestamp.fromMillis(state.subscription.trialEndDate) : null,
                createdAt: Timestamp.fromMillis(state.subscription.createdAt),
                updatedAt: Timestamp.fromMillis(state.subscription.updatedAt),
            } : null;
        },
        _selectRawSubscription: (state: UserDataSliceState) => state.subscription,
        selectAccounts: (state: UserDataSliceState) => state.accounts,
        selectSpenders: (state: UserDataSliceState) => state.spenders,
        selectFinancialCategories: (state: UserDataSliceState) => state.financialCategories,
        selectFinancialCategory: (state: UserDataSliceState, type: FinancialCategory) => {
            return (state.financialCategories as Record<string, any>)[type as keyof typeof state.financialCategories];
        }
    }
});

export const {
    setAccounts,
    setFinancialCategory,
    setSpenders,
    setSubscription
} = userDataSlice.actions;

export const {
    selectAccounts,
    selectSpenders,
    selectFinancialCategories,
    selectFinancialCategory,
    _selectRawSubscription
} = userDataSlice.selectors;

export const selectAllFinancialCategories = createSelector(
    [selectFinancialCategories],
    (financialCategories): {
        id: string;
        name: string;
        category: string;
    }[] => Object.keys(financialCategories).flatMap(
        (key) => (financialCategories[key as keyof typeof financialCategories] || []).map((item: any) => ({
            ...item,
            // category: key.toLocaleUpperCase(),
            category: (() => {
                return key.toUpperCase();
            })()
        }))
    )
);

export const selectSubscription = createSelector(
    [_selectRawSubscription],
    (subscription) => subscription ? {
        ...subscription,
        startDate: Timestamp.fromMillis(subscription.startDate),
        endDate: subscription.endDate ? Timestamp.fromMillis(subscription.endDate) : null,
        trialEndDate: subscription.trialEndDate ? Timestamp.fromMillis(subscription.trialEndDate) : null,
        createdAt: Timestamp.fromMillis(subscription.createdAt),
        updatedAt: Timestamp.fromMillis(subscription.updatedAt),
    } : null
);


export default userDataSlice.reducer;
